import React, { useCallback, useEffect, useRef, useState } from 'react';
import Wrapper from "../components/Wrapper";
import Utils from "../Utils";
import {
    addEdge,
    Background,
    Controls,
    MiniMap,
    ReactFlow,
    reconnectEdge,
    useEdgesState,
    useNodesState,
} from '@xyflow/react';

import '@xyflow/react/dist/style.css';

import Paper from "@mui/material/Paper";
import Api from "../Api";
import CustomNode from "../components/CustomNode";
import Select from "react-select";
import { toast } from "react-toastify";
import SipToShort from "../components/SipToShort";
import { dragConnect, dragConnectTypes } from "../translate";


const initialNodes = [];
const nodeTypes = {
    custom: CustomNode,
};
let sourceColorMap = {
    '55': 'blue',
    '2': 'green',
    '3': 'red',
};

const getColorForSource = (source) => sourceColorMap[source] || 'gray';
const initialEdges = [];
const customStyles = {
    menu: (provided) => ({
        ...provided,
        position: 'absolute',
        zIndex: 9999,
        maxHeight: 300,
    }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }), 
    control: (provided, state) => ({
        ...provided,
        minHeight: 44,
        borderRadius: 10,
        borderColor: state.isFocused ? '#5b7cff' : '#d9dce3',
        boxShadow: state.isFocused ? '0 0 0 3px rgba(91,124,255,0.15)' : 'none',
        '&:hover': { borderColor: '#5b7cff' },
        backgroundColor: '#fff',
    }),
    valueContainer: (provided) => ({
        ...provided,
        padding: '0 10px',
        fontSize: 14,
    }),
    placeholder: (provided) => ({
        ...provided,
        color: '#94a3b8',
        fontSize: 14,
    }),
    singleValue: (provided) => ({
        ...provided,
        color: '#1f2937',
        fontSize: 14,
    }),
};

function DragConnect(props) {
    const getLang = () => +localStorage.getItem('atsLang') || 1;
    const formatTypeLabel = (raw) => {
        const key = (raw || '').toUpperCase();
        const groupFallback = ['', 'Group', 'Группа', 'Խումբ'];
        const didFallback = ['', 'DID number', 'DID номер', 'Գլխավոր համար'];
        if (key === 'GROUP') return groupFallback[getLang()];
        if (key === 'DID') return didFallback[getLang()];
        if (dragConnectTypes[key]) return dragConnectTypes[key][getLang()];
        return key || '';
    };
    const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
    const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
    const [type, setType] = useState('')
    const [num, setNum] = useState('')
    const [isActive, setIsActive] = useState({})
    const [nums, setNums] = useState([])
    const edgeReconnectSuccessful = useRef(true);
    useEffect(() => {
        if (type) {
            (async () => {
                const { data } = await Api.type(type);
                let s = data.data.map(d => ({ value: d.number, label: `${d.number} ${d.name}` }))
                setNums(s)
            })()
        }
    }, [type])
    useEffect(() => {
        (async () => {
            const { data } = await Api.drag();
            const columnGap = 220;
            const sourceY = 40;     // top row
            const targetY = 320;    // second row (children)
            let currentX = 50;
            let position = { x: currentX, y: sourceY };
            let color = 'white'

            let fetchedEdgeDid = [];
            let fetchedNodesChild = [];
            let m = null
            let fetchedNodesDid = data.data.did.filter((s) => s.child).map((s, _) => {
                position = { x: currentX, y: sourceY };
                currentX += columnGap;
                color = Utils.getRandomColor();
                sourceColorMap = { ...sourceColorMap, [s.pref.toString() + s.did.toString() + s?.provider?.toString()]: color };
                fetchedNodesChild.push({
                    id: (s?.child?.virtual ? s?.child?.virtual?.toString() : s?.child?.buddy?.toString()) + '_' + s?.pref?.toString() + s?.did?.toString() + s?.provider?.toString(),
                    type: "custom",
                    position: { x: position.x, y: targetY },
                    data: {
                        color,
                        uid: s?.child?.id,
                        lid: (s?.child?.virtual ? s?.child?.virtual?.toString() : s?.child?.buddy?.toString()) + '_' + s?.pref?.toString() + s?.did?.toString() + s?.provider?.toString(),
                        child_type: 'SIP',
                        position: { x: position.x, y: targetY },
                        label: `${s?.child?.virtual ? s?.child?.virtual?.toString() : s?.child?.buddy?.toString()}`,
                        type: s?.child?.type?.toUpperCase(),
                        displayType: formatTypeLabel(s?.child?.type),
                        setEdges,
                        setNodes
                    }
                })

                fetchedEdgeDid.push(
                    {
                        id: s?.pref.toString() + s?.did?.toString() + s?.provider?.toString(),
                        source: s?.pref.toString() + s?.did?.toString() + s?.provider?.toString(),
                        sourceHandle: 'DID_source_' + s?.pref?.toString() + s?.did?.toString() + s?.provider?.toString() || 'source',
                        target: s?.redirect?.output + '_' + s?.pref + s?.did + s?.provider?.toString(),
                        // type: 'step',
                        targetHandle: `${s?.child?.type?.toUpperCase()}_target_` + s?.redirect?.output + '_' + s?.pref + s?.did + s?.provider?.toString() || 'target',
                    }
                )
                return ({
                    id: s?.pref?.toString() + s?.did?.toString() + s?.provider?.toString(),
                    type: "custom",
                    position,
                    data: {
                        color,
                        label: `${s?.pref + s?.did} ${s?.notes}`,
                        type: 'DID',
                        displayType: formatTypeLabel('DID')
                    }
                })
            })
            setNodes([...fetchedNodesDid, ...fetchedNodesChild])
            setEdges([...fetchedEdgeDid,])

        })()
    }, [])
    const onReconnectStart = useCallback(() => {
        edgeReconnectSuccessful.current = false;
    }, []);

    const onReconnect = useCallback((oldEdge, newConnection) => {
        edgeReconnectSuccessful.current = true;
        setEdges((els) => reconnectEdge(oldEdge, newConnection, els));
    }, []);

    const onReconnectEnd = useCallback((_, edge) => {
        if (!edgeReconnectSuccessful.current) {
            setEdges((eds) => eds.filter((e) => e.id !== edge.id));
        }

        edgeReconnectSuccessful.current = true;
    }, []);
    const onConnect = useCallback(async (params) => {
        try {
            let { sourceHandle, source, target, targetHandle } = params;
            let from = sourceHandle.split('_')[0]
            let to = targetHandle.split('_')[0]
            let start = source.split('_')[0]
            let end = target.split('_')[0]
            if (from === 'SIP') {
                start = source.split('_')[1]
            }
            if (from === to) {
                return
            } else if ((from === 'GROUP' && to === 'SIP') || (from === 'IVR' && to === 'SIP')) {
                setIsActive({ from, to, start, end })
            } else {

                await Api.dragCreate({ from, to, start, end })
                toast.success(dragConnect.success[getLang()] || 'Success')
            }
            setEdges((eds) => addEdge(params, eds))

        } catch (e) {
            toast.error(dragConnect.error[getLang()] || 'Something went wrong')
        }
    },
        [setEdges]);
    const coloredEdges = edges.map((edge) => ({
        ...edge,
        style: { stroke: getColorForSource(edge.source), strokeWidth: 1, animated: true },
    }));
    const visibleNodes = nodes.filter((n) => n?.data?.label && n.data.label !== 'undefined');

    const handleChange = useCallback((val) => {
        let color = Utils.getRandomColor();
        const newNode = {
            id: `${val}`,
            type: 'custom',
            position: { x: nodes.length * 220 + 60, y: 40 },
            data: {
                color,
                label: val,
                type: type.toUpperCase(),
                displayType: formatTypeLabel(type),
                uid: val,
                lid: `${val}`,
                child_type: type.toUpperCase() || 'SIP',
                position: { x: nodes.length * 220 + 60, y: 320 },
                setNodes: setNodes,
                setEdges: setEdges
            },

        }
        setNodes((prevNodes) => [...prevNodes, newNode]);
        setNum(val)
    }, [type, nodes])

    return (
        <Wrapper>
            <div className="tableParent">
                <Paper sx={{ minWidth: 'fit-content', fontSize: 10, borderRadius: '14px', overflow: 'hidden', boxShadow: '0 12px 32px rgba(15, 23, 42, 0.09)' }}>
                    <div style={{padding: '14px 20px', borderBottom: '1px solid #e6e8ee', }}>
                        <h1 className="m-0" style={{ fontSize: 20, fontWeight: 700, color: '#111827' }}>{dragConnect.controllers[getLang()]}</h1>
                    </div>
                    <div className="control" style={{padding: '18px 20px 14px',borderBottom: '1px solid #e6e8ee'}}>
                        <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16}}>
                            <div style={{display: 'flex', flexDirection: 'column', gap: 6}}>
                                <span style={{fontSize: 13, fontWeight: 700, color: '#334155', textTransform: 'uppercase', letterSpacing: 0.3}}>{dragConnect.chooseType[getLang()]}</span>
                                <select
                                    value={type}
                                    onChange={(ev) => setType(ev.target.value)}
                                    style={{
                                        width: '100%',
                                        padding: '11px 12px',
                                        borderRadius: 10,
                                        border: '1px solid #d9dce3',
                                        background: '#fff',
                                        fontSize: 14,
                                        color: '#111827',
                                        boxShadow: '0 1px 2px rgba(15, 23, 42, 0.05)',
                                        outline: 'none'
                                    }}
                                >
                                    <option value="">
                                        {dragConnect.select[getLang()]}
                                    </option>
                                    <option value="did">
                                        {dragConnect.didNumber[getLang()]}
                                    </option>
                                    <option value="sip">
                                        {dragConnect.sipNumber[getLang()]}
                                    </option>
                                    <option value="short">
                                        {dragConnect.shortNumber[getLang()]}
                                    </option>
                                    <option value="ivr">
                                        {dragConnect.ivr[getLang()]}
                                    </option>
                                    <option value="included">
                                        {dragConnect.includedNumber[getLang()]}
                                    </option>
                                </select>
                            </div>
                            <div style={{display: 'flex', flexDirection: 'column', gap: 6}}>
                                <span style={{fontSize: 13, fontWeight: 700, color: '#334155', textTransform: 'uppercase', letterSpacing: 0.3}}>{dragConnect.chooseElement[getLang()]}</span>
                                <Select
                                    styles={customStyles}
                                    menuPlacement="auto"
                                    menuPosition="fixed"
                                    onChange={(ev) => handleChange(ev.value)}
                                    menuPortalTarget={document.body}
                                    placeholder={dragConnect.chooseElement[getLang()] || dragConnect.select[getLang()]}
                                    className="basic-single"
                                    classNamePrefix="select"
                                    isSearchable={true}
                                    isDisabled={!type}
                                    value={type ? { value: num, label: num } : null}
                                    name="color"
                                    options={[{ value: '', label: dragConnect.select[getLang()] }, ...nums]}
                                />
                            </div>
                        </div>
                    </div>
                    <div style={{ width: '100%', height: '100vh' }}>
                        <ReactFlow
                            nodes={visibleNodes}
                            edges={coloredEdges}
                            onNodesChange={onNodesChange}
                            onEdgesChange={onEdgesChange}
                            onConnect={onConnect}
                            nodeTypes={nodeTypes}
                            onReconnect={onReconnect}
                            onReconnectStart={onReconnectStart}
                            onReconnectEnd={onReconnectEnd}
                        >
                            <Controls />
                            <MiniMap />
                        </ReactFlow>
                    </div>
                </Paper>
                {isActive.start ? <SipToShort open={isActive} setOpen={setIsActive} /> : null}
            </div>
        </Wrapper>
    );
}

export default DragConnect;

